import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { fleetEqualizationKeys, fleetReportKeys, wingWeighingKeys } from "../query-keys";
import type { BoatId, WingConfig, WingWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { wingWeighingKeys } from "../query-keys";

type WingWeighingCreate = components["schemas"]["WingWeighingCreate"];
type WingWeighingUpdate = components["schemas"]["WingWeighingUpdate"];

export function useWingWeighings(boatId: BoatId, config?: WingConfig) {
  return useQuery({
    queryKey: config
      ? wingWeighingKeys.byBoatAndConfig(boatId, config)
      : wingWeighingKeys.byBoat(boatId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/boats/{boat_id}/wing-weighings",
        {
          params: {
            path: { boat_id: boatId },
            query: config ? { config } : undefined,
          },
        },
      );
      return throwIfError(result, "Failed to fetch wing weighings");
    },
    enabled: !!boatId,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, boatId: BoatId) {
  qc.invalidateQueries({ queryKey: wingWeighingKeys.byBoat(boatId) });
  qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
  qc.invalidateQueries({ queryKey: fleetReportKeys.all });
}

export function useCreateWingWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: WingWeighingCreate) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/wing-weighings",
        { params: { path: { boat_id: boatId } }, body: data },
      );
      return throwIfError(result, "Failed to create wing weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useUpdateWingWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      weighingId,
      data,
    }: {
      weighingId: WingWeighingId;
      data: WingWeighingUpdate;
    }) => {
      const result = await api.PATCH(
        "/api/boats/{boat_id}/wing-weighings/{weighing_id}",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
          body: data,
        },
      );
      return throwIfError(result, "Failed to update wing weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useSignOffWingWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weighingId: WingWeighingId) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/wing-weighings/{weighing_id}/sign-off",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
        },
      );
      return throwIfError(result, "Failed to sign off wing weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useUnlockWingWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weighingId: WingWeighingId) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/wing-weighings/{weighing_id}/unlock",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
        },
      );
      return throwIfError(result, "Failed to unlock wing weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useDeleteWingWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weighingId: WingWeighingId) => {
      const result = await api.DELETE(
        "/api/boats/{boat_id}/wing-weighings/{weighing_id}",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
        },
      );
      return throwIfError(result, "Failed to delete wing weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}
